import { createRoute, redirect } from "@tanstack/react-router";
import { languages } from "../utils/languages";
import { rootRoute } from "./root";

const isSupportedLocale = (locale: string) =>
  languages.some((language) => language.locale === locale);

// Matches locale-shaped segments like "en", "EN-GB", "pt-BR"
const LOCALE_SHAPED_REGEX = /^[a-z]{2}(-[a-z]{2})?$/i;

export const localeRoute = createRoute({
  path: "v2/$locale",
  getParentRoute: () => rootRoute,
  beforeLoad: ({ params }) => {
    if (isSupportedLocale(params.locale)) {
      return;
    }

    const urlParts = window.location.pathname.split("/");
    const lowercaseLocale = params.locale.toLowerCase();

    // Mixed-case locale (e.g. "en-US" from partner links): redirect to lowercase
    if (isSupportedLocale(lowercaseLocale)) {
      const remainingPath = urlParts.slice(3).join("/");
      throw redirect({
        to: "/v2/$locale/" + remainingPath + window.location.search,
        params: {
          locale: lowercaseLocale,
        },
      });
    }

    // Language-only locale (e.g. "it"): redirect to its doubled form ("it-it")
    const doubledLocale = lowercaseLocale + "-" + lowercaseLocale;
    if (isSupportedLocale(doubledLocale)) {
      const remainingPath = urlParts.slice(3).join("/");
      throw redirect({
        to: "/v2/$locale/" + remainingPath + window.location.search,
        params: {
          locale: doubledLocale,
        },
      });
    }

    // Unsupported locale with a known language part (e.g. "en-GB", "pt-PT"):
    // redirect to the closest supported locale ("en-us", "pt-br")
    const languagePart = lowercaseLocale.split("-")[0];
    const closestLocale = languages.find(
      (language) => language.locale.split("-")[0] === languagePart,
    )?.locale;
    if (LOCALE_SHAPED_REGEX.test(params.locale) && closestLocale) {
      const remainingPath = urlParts.slice(3).join("/");
      throw redirect({
        to: "/v2/$locale/" + remainingPath + window.location.search,
        params: {
          locale: closestLocale,
        },
      });
    }

    const defaultLocale = languages[0]?.locale ?? "en-us";
    // Unknown locale-shaped segment (e.g. "xx-XX"): replace it with the default
    // locale. Anything else (e.g. "/v2/search") is a path — keep it as-is.
    const remainingPath = urlParts
      .slice(LOCALE_SHAPED_REGEX.test(params.locale) ? 3 : 2)
      .join("/");
    throw redirect({
      to: "/v2/$locale/" + remainingPath + window.location.search,
      params: {
        locale: defaultLocale,
      },
    });
  },
});
